import { Controller } from '@hotwired/stimulus';
import { mountVueAppInto } from 'shared/vue/mountVueApp';
import alertify from 'shared/alertify';

// APIから取得した`shopifyDiscountAttributes`を元に、`Discount`オブジェクトを生成する
// 型定義:
// ```ts
// type Discount = DiscountPercentage | DiscountAmount | DiscountFreeShipping
//
// type DiscountPercentage = {
//   discountType: 'percentage'
//   percentage: number
// }
// type DiscountAmount = {
//   discountType: 'amount'
//   price: number
// }
// type DiscountFreeShipping = DiscountFreeShippingAll | DiscountFreeShippingHasMaxShippingPrice
//
// type DiscountFreeShippingAll = {
//   discountType: 'free_shipping'
//   shipping_discount_type: 'all'
// }
// type DiscountFreeShippingHasMaxShippingPrice = {
//   discountType: 'free_shipping'
//   shipping_discount_type: 'has_max_shipping_price'
//   max_shipping_price: number
// }
// ```
const buildDiscountFrom = (shopifyDiscountAttributes) => {
  const discountType = shopifyDiscountAttributes.discount_type || 'percentage';
  switch (discountType) {
    case 'percentage':
      return {
        discountType,
        percentage: shopifyDiscountAttributes.percentage || 0,
      };
    case 'amount':
      return { discountType, price: shopifyDiscountAttributes.price || 0 };
    case 'free_shipping':
      if (shopifyDiscountAttributes.max_shipping_price) {
        return {
          discountType,
          shippingDiscountType: 'has_max_shipping_price',
          maxShippingPrice: shopifyDiscountAttributes.max_shipping_price,
        };
      }
      return { discountType, shippingDiscountType: 'all' };
    default:
      throw new Error(`Unexpected discountType: '${discountType}'`);
  }
};

// APIから取得した`shopifyDiscountAttributes`を元に、`Requirement`オブジェクトを生成する
// 型定義:
// ```ts
// type Requirement = RequirementUnconditional | RequirementSubtotal | RequirementQuantity
//
// type RequirementUnconditional = { requirementType: 'unconditional' }
// type RequirementSubtotal = {
//   requirementType: 'unconditional'
//   requiredSubtotal: number
// }
// type RequirementQuantity = {
//   requirementType: 'unconditional'
//   requiredQuantity: number
// }
// ```
const buildRequirementFrom = (shopifyDiscountAttributes) => {
  const requirementType =
    shopifyDiscountAttributes.requirement || 'unconditional';
  switch (requirementType) {
    case 'unconditional':
      return { requirementType };
    case 'subtotal':
      return {
        requirementType,
        requiredSubtotal: shopifyDiscountAttributes.required_subtotal || 0,
      };
    case 'quantity':
      return {
        requirementType,
        requiredQuantity: shopifyDiscountAttributes.required_quantity || 0,
      };
    default:
      throw new Error(`Unexpected requirementType: '${requirementType}'`);
  }
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// 面共通コンポーネント(タイトル)
// ------------------------------------------------------------------------------
const coupon_title_field = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
              <legend class="c-formLabel">
                  クーポン名
                  <span class="c-formLabel__required">*</span>
              </legend>
              <div class="c-textField">
                  <input type="text" name="coupon[title]" id="coupon_title" v-model="titleModel" class="is-required">
              </div>
          </fieldset>
      </div>
      `,
  props: {
    title: String,
  },
  computed: {
    titleModel: {
      get: function () {
        return this.title;
      },
      set: function (val) {
        this.$emit('update:title', val);
      },
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// クーポン作成画面共通コンポーネント(説明・注記)
// ------------------------------------------------------------------------------
const coupon_description_field = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
              <legend class="c-formLabel">
                  説明・注記
                  <span class="c-formLabel__required">*</span>
              </legend>
              <div class="c-textArea">
                  <textarea rows="3" name="coupon[description]" id="coupon_description" v-model="descriptionModel" class="is-required"></textarea>
              </div>
          </fieldset>
      </div>
      `,
  props: {
    description: String,
  },
  computed: {
    descriptionModel: {
      get: function () {
        return this.description;
      },
      set: function (val) {
        this.$emit('update:description', val);
      },
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// クーポン作成画面共通コンポーネント(アイキャッチ画像)
// ------------------------------------------------------------------------------
const coupon_picture_field = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
              <legend class="c-formLabel">
                  アイキャッチ画像
              </legend>
              <label class="c-imageField">
                  <input accept="image/*" type="file" name="coupon[picture]" @change="changePicture" id="coupon_picture" class="c-imageField__input">
                  <span class="c-imageField__empty" v-if="!hasPictureUrl">
                      <i class="fa fa-camera c-imageField__icon"></i>
                      <span class="c-imageField__message">画像ファイルをアップロード</span>
                  </span>
                  <img :src="pictureUrl" class="c-imageField__preview" v-if="hasPictureUrl" />
                  <button type="button" class="c-imageField__delete" v-if="hasPictureUrl" @click="clearPicture">削除</button>
                  <input id="coupon_picture_delete" name="coupon[picture_delete]" v-model="deletedPicture" type="hidden">
              </label>
              <p>※&nbsp;縦:396px、横:640px にリサイズされます</p>
              <p v-if="maxSizeText">※&nbsp;ファイルサイズは最大 {{ maxSizeText }}</p>
          </fieldset>
      </div>
      `,
  props: {
    pictureUrl: String,
    // プッシュ通知本体のアイキャッチ欄と表記を揃えるため、number_to_human_size で
    // 整形済みの文字列を ERB から受け取る
    maxSizeText: String,
  },
  data: () => ({
    deletedPicture: false,
  }),
  computed: {
    hasPictureUrl: function () {
      return this.pictureUrl && this.pictureUrl !== '';
    },
  },
  methods: {
    changePicture: function (e) {
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) {
        this.$emit('update:pictureUrl', null);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        this.$emit('update:pictureUrl', e.target.result);
        this.deletedPicture = false;
      };
      reader.readAsDataURL(files[0]);
    },
    clearPicture: function () {
      this.$emit('update:pictureUrl', null);
      this.deletedPicture = true;
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// クーポン作成画面共通コンポーネント(有効期限)
// ------------------------------------------------------------------------------
const coupon_expire_field = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
              <legend class="c-formLabel">有効期限のタイプ</legend>
              <div class="c-select">
                  <select name="coupon[expire_type]" v-model="expireTypeModel" id="coupon_expire_type" :disabled="disabled">
                      <option v-for="option in expireTypeOptions" :value="option.value">{{ option.text }}</option>
                  </select>
                  <input type="hidden" name="coupon[expire_type]" v-model="expireType" v-if="disabled">
                  <div class="c-select__icon"></div>
              </div>
          </fieldset>
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8" v-if="isExpireTypeFirst">
              <legend class="c-formLabel">
                  有効期限（日）
                  <span class="c-formLabel__required">*</span>
              </legend>
              <div class="c-numberField">
                  <input min="1" type="number" name="coupon[expire_day]" v-model.number="expireDayModel" id="coupon_expire_day" class="is-required" :disabled="disabled">
                  <input type="hidden" name="coupon[expire_day]" v-model="expireDay" v-if="disabled">
                  <p class="suffixField">日間</p>
              </div>
          </fieldset>
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8 date" v-if="isExpireTypeDate">
              <legend class="c-formLabel">有効期限（日）</legend>
              <div class="p-dateTimeSelector">
                  <div class="p-dateTimeSelector__items">
                      <select id="coupon_expire_date_1i" name="coupon[expire_date(1i)]" v-model="expireDateYearModel" :disabled="disabled">
                          <option v-for="option in expireDateYearOptions" :value="option.value">{{ option.text }}</option>
                      </select>
                      <input type="hidden" name="coupon[expire_date(1i)]" v-model="expireDateYear" v-if="disabled">
                      /
                      <select id="coupon_expire_date_2i" name="coupon[expire_date(2i)]" v-model="expireDateMonthModel" :disabled="disabled">
                          <option v-for="option in expireDateMonthOptions" :value="option.value">{{ option.text }}</option>
                      </select>
                      <input type="hidden" name="coupon[expire_date(2i)]" v-model="expireDateMonth" v-if="disabled">
                      /
                      <select id="coupon_expire_date_3i" name="coupon[expire_date(3i)]" v-model="expireDateDayModel" :disabled="disabled">
                          <option v-for="option in expireDateDayOptions" :value="option.value">{{ option.text }}</option>
                      </select>
                      <input type="hidden" name="coupon[expire_date(3i)]" v-model="expireDateDay" v-if="disabled">
                  </div>
              </div>
          </fieldset>

      </div>
      `,
  props: {
    expireDate: Date,
    expireDay: Number,
    expireType: String,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    expireDateDay: null,
    expireDateMonth: null,
    expireDateYear: null,
  }),
  created: function () {
    if (this.expireDate && this.expireDate !== '') {
      const convertedExpireDate = new Date(this.expireDate);
      this.expireDateDay = convertedExpireDate.getDate();
      this.expireDateMonth = convertedExpireDate.getMonth() + 1;
      this.expireDateYear = convertedExpireDate.getFullYear();
    } else {
      const now = new Date();
      this.expireDateDay = now.getDate();
      this.expireDateMonth = now.getMonth() + 1;
      this.expireDateYear = now.getFullYear();
    }
  },
  computed: {
    isExpireTypeFirst: function () {
      return this.expireType === 'first';
    },
    isExpireTypeDate: function () {
      return this.expireType === 'date';
    },
    expireDateDayOptions: () => {
      const options = [];
      for (let i = 1; i <= 31; i++) {
        options.push({ value: i, text: i });
      }
      return options;
    },
    expireDateMonthOptions: () => {
      const options = [];
      for (let i = 1; i <= 12; i++) {
        options.push({ value: i, text: i });
      }
      return options;
    },
    expireDateYearOptions: () => {
      const options = [];
      const currentYear = new Date().getFullYear();
      for (let i = currentYear - 1; i <= currentYear + 10; i++) {
        options.push({ value: i, text: i });
      }
      return options;
    },
    expireTypeOptions: () => [
      { value: 'first', text: '発行日からの日数' },
      { value: 'date', text: '年月日' },
      { value: 'none', text: 'なし' },
    ],
    expireTypeModel: {
      get: function () {
        return this.expireType;
      },
      set: function (val) {
        this.updateExpireInfo(val, this.expireDay, this.expireDate);
      },
    },
    expireDayModel: {
      get: function () {
        return this.expireDay;
      },
      set: function (val) {
        this.updateExpireInfo(this.expireType, val, this.expireDate);
      },
    },
    expireDateYearModel: {
      get: function () {
        return this.expireDateYear;
      },
      set: function (val) {
        const newExpireDate = this.calculateNewExpireDate(
          val,
          this.expireDateMonth,
          this.expireDateDay,
        );
        this.updateExpireInfo(this.expireType, this.expireDay, newExpireDate);
      },
    },
    expireDateMonthModel: {
      get: function () {
        return this.expireDateMonth;
      },
      set: function (val) {
        const newExpireDate = this.calculateNewExpireDate(
          this.expireDateYear,
          val,
          this.expireDateDay,
        );
        this.updateExpireInfo(this.expireType, this.expireDay, newExpireDate);
      },
    },
    expireDateDayModel: {
      get: function () {
        return this.expireDateDay;
      },
      set: function (val) {
        const newExpireDate = this.calculateNewExpireDate(
          this.expireDateYear,
          this.expireDateMonth,
          val,
        );
        this.updateExpireInfo(this.expireType, this.expireDay, newExpireDate);
      },
    },
  },
  methods: {
    calculateNewExpireDate: (year, month, day) =>
      new Date(year, month - 1, day),
    updateExpireInfo: function (expireType, expireDay, expireDate) {
      this.$emit('update:expireType', expireType);
      this.$emit('update:expireDate', expireDate);
      this.$emit('update:expireDay', expireDay);
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// Shopifyクーポン設定画面共通コンポーネント(割引タイプ)
// ------------------------------------------------------------------------------
const shopify_discount_type_fields = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
              <legend class="c-formLabel">
                  タイプ
              </legend>
              <div class="c-select">
                  <select name="coupon[external_coupon_attributes][discount_type]" v-model="discountType" id="coupon_external_coupon_attributes_discount_type" :disabled="isPersisted">
                      <option v-for="option in discountTypeOptions" :value="option.value">{{ option.text }}</option>
                  </select>
                  <input type="hidden" name="coupon[external_coupon_attributes][discount_type]" :value="discountType" v-if="isPersisted" />
                  <div class="c-select__icon"></div>
              </div>
          </fieldset>
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8" v-if="isDiscountTypePercentage">
              <legend class="c-formLabel">
                  値
                  <span class="c-formLabel__required">*</span>
              </legend>
              <p>ディスカウント価格</p>
              <div class="c-numberField">
                  <input min="1" max="100" type="number" name="coupon[external_coupon_attributes][discount_value]" v-model="percentage" :disabled="isPersisted" id="coupon_percentage" class="is-required">
                  <input type="hidden" name="coupon[external_coupon_attributes][discount_value]" v-model="percentage" v-if="isPersisted">
                  <p class="suffixField">%</p>
              </div>
          </fieldset>
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8" v-if="isDiscountTypeAmount">
              <legend class="c-formLabel">
                  値
                  <span class="c-formLabel__required">*</span>
              </legend>
              <p>ディスカウント価格</p>
              <div class="c-numberField">
                  <input min="1" type="number" name="coupon[external_coupon_attributes][discount_value]" v-model="price" :disabled="isPersisted" id="coupon_price" class="is-required">
                  <input type="hidden" name="coupon[external_coupon_attributes][discount_value]" v-model="price" v-if="isPersisted">
                  <p class="suffixField">円</p>
              </div>
          </fieldset>
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8" v-if="isDiscountTypeFreeShipping">
              <legend class="c-formLabel">
                  配送料
              </legend>
              <div class="c-select">
                  <select id="coupon_shipping_type" v-model="shippingDiscountType" :disabled="isPersisted">
                      <option v-for="option in shippingDiscountTypeOptions" :value="option.value">{{ option.text }}</option>
                  </select>
                  <div class="c-select__icon"></div>
              </div>
              <div class="c-formSubLabel" v-if="isShippingDiscountTypeHasMaxShippingPrice">
                  <p style="margin-top: 20px;">
                      金額
                      <span class="c-formLabel__required">*</span>
                  </p>
                  <div class="c-numberField">
                      <input min="1" type="number" name="coupon[external_coupon_attributes][max_shipping_price]" v-model="maxShippingPrice" :disabled="isPersisted" id="coupon_max_shipping_price" class="is-required">
                      <input type="hidden" name="coupon[external_coupon_attributes][max_shipping_price]" v-model="maxShippingPrice" v-if="isPersisted" />
                      <p class="suffixField">円</p>
                  </div>
              </div>
          </fieldset>
      </div>
      `,
  props: {
    discount: {},
    isPersisted: Boolean,
  },
  data: () => ({
    discountType: 'percentage',
    percentage: null,
    price: null,
    shippingDiscountType: 'all',
    maxShippingPrice: null,
  }),
  created() {
    this.mapDiscountToData();
  },
  watch: {
    discount() {
      this.mapDiscountToData();
    },
    discountType() {
      this.emitDiscount();
    },
    percentage() {
      this.emitDiscount();
    },
    price() {
      this.emitDiscount();
    },
    shippingDiscountType() {
      this.emitDiscount();
    },
    maxShippingPrice() {
      this.emitDiscount();
    },
  },
  computed: {
    discountTypeOptions() {
      return [
        { value: 'percentage', text: '割引率' },
        { value: 'amount', text: '定額' },
        { value: 'free_shipping', text: '無料配送' },
      ];
    },
    shippingDiscountTypeOptions() {
      return [
        { value: 'all', text: '全ての金額' },
        {
          value: 'has_max_shipping_price',
          text: '一定額以上の配送料を除外する',
        },
      ];
    },
    isDiscountTypePercentage() {
      return this.discountType === 'percentage';
    },
    isDiscountTypeAmount() {
      return this.discountType === 'amount';
    },
    isDiscountTypeFreeShipping() {
      return this.discountType === 'free_shipping';
    },
    isShippingDiscountTypeHasMaxShippingPrice() {
      return this.shippingDiscountType === 'has_max_shipping_price';
    },
  },
  methods: {
    mapDiscountToData() {
      this.discountType = this.discount.discountType;
      this.percentage = this.discount.percentage;
      this.price = this.discount.price;
      this.shippingDiscountType = this.discount.shippingDiscountType || 'all';
      this.maxShippingPrice = this.discount.maxShippingPrice;
    },
    buildDiscount() {
      const discountBase = { discountType: this.discountType };

      switch (discountBase.discountType) {
        case 'percentage':
          return { ...discountBase, percentage: this.percentage };
        case 'amount':
          return { ...discountBase, price: this.price };
        case 'free_shipping': {
          const freeShippingBase = {
            ...discountBase,
            shippingDiscountType: this.shippingDiscountType,
          };

          switch (freeShippingBase.shippingDiscountType) {
            case 'all':
              return freeShippingBase;
            case 'has_max_shipping_price':
              return {
                ...freeShippingBase,
                maxShippingPrice: this.maxShippingPrice,
              };
            default:
              throw new Error(
                `Unexpected shippingDiscountType: '${freeShippingBase.shippingDiscountType}'`,
              );
          }
        }
        default:
          throw new Error(`Unexpected discountType: '${discountBase}'`);
      }
    },
    emitDiscount() {
      this.$emit('update:discount', this.buildDiscount());
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// Shopifyクーポン設定画面共通コンポーネント(最小要件)
// ------------------------------------------------------------------------------
const shopify_requirement_field = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
              <legend class="c-formLabel">
                  最小要件
              </legend>
              <div class="c-select">
                  <select name="coupon[external_coupon_attributes][requirement_type]" v-model="requirementType" :disabled="isPersisted" id="coupon_requirement">
                      <option v-for="option in requirementOptions" :value="option.value">{{ option.text }}</option>
                  </select>
                  <input type="hidden" name="coupon[external_coupon_attributes][requirement_type]" v-model="requirementType" v-if="isPersisted" />
                  <div class="c-select__icon"></div>
              </div>
              <div class="c-formSubLabel" v-if="isRequirementSubtotal">
                  <p style="margin-top: 20px;">
                      金額
                      <span class="c-formLabel__required">*</span>
                  </p>
                  <div class="c-numberField">
                      <input min="1" type="number" name="coupon[external_coupon_attributes][requirement_value]" v-model="requiredSubtotal" :disabled="isPersisted" id="coupon_required_subtotal" class="is-required">
                      <input type="hidden" name="coupon[external_coupon_attributes][requirement_value]" v-model="requiredSubtotal" v-if="isPersisted" />
                      <p class="suffixField">円</p>
                  </div>
              </div>
              <div class="c-formSubLabel" v-if="isRequirementQuantity">
                  <p style="margin-top: 20px;">
                      数量
                      <span class="c-formLabel__required">*</span>
                  </p>
                  <div class="c-numberField">
                      <input min="1" type="number" name="coupon[external_coupon_attributes][requirement_value]" v-model="requiredQuantity" :disabled="isPersisted" id="coupon_required_quantity" class="is-required">
                      <input type="hidden" name="coupon[external_coupon_attributes][requirement_value]" v-model="requiredQuantity" v-if="isPersisted" />
                      <p class="suffixField">個</p>
                  </div>
              </div>
          </fieldset>
      </div>
      `,
  props: {
    requirement: {},
    isPersisted: Boolean,
  },
  data: () => ({
    requirementType: 'unconditional',
    requiredSubtotal: null,
    requiredQuantity: null,
  }),
  created() {
    this.mapRequirementToData();
  },
  watch: {
    requirement() {
      this.mapRequirementToData();
    },
    requirementType() {
      this.emitRequirement();
    },
    requiredSubtotal() {
      this.emitRequirement();
    },
    requiredQuantity() {
      this.emitRequirement();
    },
  },
  computed: {
    requirementOptions() {
      return [
        { value: 'unconditional', text: 'なし' },
        { value: 'subtotal', text: '最低購入金額(￥)' },
        { value: 'quantity', text: '最低購入数量' },
      ];
    },
    isRequirementSubtotal() {
      return this.requirementType === 'subtotal';
    },
    isRequirementQuantity() {
      return this.requirementType === 'quantity';
    },
  },
  methods: {
    mapRequirementToData() {
      this.requirementType = this.requirement.requirementType;
      this.requiredSubtotal = this.requirement.requiredSubtotal;
      this.requiredQuantity = this.requirement.requiredQuantity;
    },
    buildRequirement() {
      const base = { requirementType: this.requirementType };
      switch (base.requirementType) {
        case 'unconditional':
          return base;
        case 'subtotal':
          return { ...base, requiredSubtotal: this.requiredSubtotal };
        case 'quantity':
          return { ...base, requiredQuantity: this.requiredQuantity };
        default:
          throw new Error(
            `Unexpected requirementType: '${this.requirementType}'`,
          );
      }
    },
    emitRequirement() {
      this.$emit('update:requirement', this.buildRequirement());
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// Shopifyクーポン設定画面共通コンポーネント(Shopifyクーポン利用後の遷移先URL)
// ------------------------------------------------------------------------------
const shopify_redirect_path_field = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__12 p-formA__item__l__12">
              <legend class="c-formLabel">
                  Shopifyクーポン利用後の遷移先URL
              </legend>
              <div class="c-textFieldWithPlaceholder">
                  <div class="placeholder">
                      https://example.myshopify.com/
                  </div>
                  <div class="text-field">
                      <input type="text" name="coupon[external_coupon_attributes][redirect_path]" v-model="redirectPathModel" id="coupon_redirect_path">
                  </div>
              </div>
          </fieldset>
      </div>
      `,
  props: {
    redirectPath: String,
  },
  computed: {
    redirectPathModel: {
      get() {
        return this.redirectPath;
      },
      set(value) {
        this.$emit('update:redirectPath', value);
      },
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// 通常クーポン設定画面共通コンポーネント(クーポン消込コード)
// ------------------------------------------------------------------------------
const default_coupon_confirm_code_field = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
              <legend class="c-formLabel">
                  クーポン消込コード
              </legend>
              <div class="c-select">
                  <select name="coupon[confirm_code]" v-model="confirmCodeModel" id="coupon_confirm_code">
                      <option v-for="option in confirmCodeOptions" :value="option.value">{{ option.text }}</option>
                  </select>
                  <div class="c-select__icon"></div>
              </div>
              <p class="u-mt5">
                  ※&nbsp;利用時に
                  <a target="blank" href="/shops/shop_branches">クーポン消込コード</a>
                  による認証が必要となります
              </p>
          </fieldset>
      </div>
      `,
  props: {
    confirmCode: Boolean,
  },
  computed: {
    confirmCodeModel: {
      get() {
        return this.confirmCode;
      },
      set(value) {
        this.$emit('update:confirmCode', value);
      },
    },
    confirmCodeOptions() {
      return [
        { value: false, text: 'なし' },
        { value: true, text: 'あり' },
      ];
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// 通常クーポン設定画面共通コンポーネント(1端末1回利用制限)
// ------------------------------------------------------------------------------
const default_coupon_restricted_once_field = {
  template: `
      <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
              <legend class="c-formLabel">
                  1端末1回利用制限
              </legend>
              <div class="c-select">
                  <select name="coupon[restricted_once]" v-model="restrictedOnceModel" id="coupon_restricted_once">
                      <option v-for="option in restrictedOnceOptions" :value="option.value">{{ option.text }}</option>
                  </select>
                  <div class="c-select__icon"></div>
              </div>
          </fieldset>
      </div>
      `,
  props: {
    restrictedOnce: String,
  },
  computed: {
    restrictedOnceModel: {
      get() {
        return this.restrictedOnce;
      },
      set(value) {
        this.$emit('update:restrictedOnce', value);
      },
    },
    restrictedOnceOptions() {
      return [
        { value: 'restricted_once_auto', text: '自動設定（推奨）' },
        { value: 'restricted_once_disabled', text: 'なし' },
        { value: 'restricted_once_enabled', text: 'あり' },
      ];
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// 通常クーポン設定画面共通コンポーネント(バーコードタイプ)
// ------------------------------------------------------------------------------
const default_coupon_code_type_field = {
  template: `
      <div class="p-formA__items">
           <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
               <legend class="c-formLabel">
                   バーコードタイプ
               </legend>
               <div class="c-select">
                   <select name="coupon[code_type]" v-model="codeTypeModel" id="coupon_code_type">
                       <option v-for="option in codeTypeOptions" :value="option.value">{{ option.text }}</option>
                   </select>
                   <div class="c-select__icon"></div>
               </div>
           </fieldset>
       </div>
      `,
  props: {
    codeType: String,
  },
  computed: {
    codeTypeModel: {
      get() {
        return this.codeType;
      },
      set(value) {
        this.$emit('update:codeType', value);
      },
    },
    codeTypeOptions() {
      return [
        { value: '', text: '-' },
        { value: 'code39', text: 'CODE39' },
      ];
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// STORES ECクーポン設定画面
// ------------------------------------------------------------------------------
const stores_retail_coupon_service = {
  components: {
    'coupon-title-field': coupon_title_field,
    'coupon-description-field': coupon_description_field,
    'coupon-picture-field': coupon_picture_field,
    'coupon-expire-field': coupon_expire_field,
  },
  template: `
      <div class="p-formA">
          <input type="hidden" name="coupon[external_coupon_attributes][id]" id="coupon_external_coupon_attributes_id" :value="storesRetailCouponId" />

          <coupon-title-field :title="title" @update:title="$emit('update:title', $event)" />
          <coupon-description-field :description="description" @update:description="$emit('update:description', $event)" />
          <coupon-picture-field :picture-url="pictureUrl" :max-size-text="pictureMaxSizeText" @update:pictureUrl="$emit('update:pictureUrl', $event)" />

          <div class="p-formA__items">
              <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
                  <legend class="c-formLabel">
                      内容
                  </legend>
                  <div class="c-select">
                      <select name="coupon[external_coupon_attributes][discount_type]" v-model="discountType" id="coupon_external_coupon_attributes_discount_type" :disabled="isPersisted">
                          <option v-for="option in discountTypeOptions" :value="option.value">{{ option.text }}</option>
                      </select>
                      <input type="hidden" name="coupon[external_coupon_attributes][discount_type]" :value="discountType" v-if="isPersisted" />
                      <div class="c-select__icon"></div>
                  </div>
              </fieldset>
          </div>
          <div class="p-formA__items">
              <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8" v-if="isDiscountTypePercentage">
                  <legend class="c-formLabel">
                      値
                      <span class="c-formLabel__required">*</span>
                  </legend>
                  <p>ディスカウント価格</p>
                  <div class="c-numberField">
                      <input min="1" max="100" type="number" name="coupon[external_coupon_attributes][discount_value]" v-model="percentage" :disabled="isPersisted" id="coupon_percentage" class="is-required">
                      <input type="hidden" name="coupon[external_coupon_attributes][discount_value]" v-model="percentage" v-if="isPersisted">
                      <p class="suffixField">%</p>
                  </div>
              </fieldset>
              <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8" v-if="isDiscountTypeAmount">
                  <legend class="c-formLabel">
                      値
                      <span class="c-formLabel__required">*</span>
                  </legend>
                  <p>ディスカウント価格</p>
                  <div class="c-numberField">
                      <input min="1" type="number" name="coupon[external_coupon_attributes][discount_value]" v-model="amount" :disabled="isPersisted" id="coupon_price" class="is-required">
                      <input type="hidden" name="coupon[external_coupon_attributes][discount_value]" v-model="amount" v-if="isPersisted">
                      <p class="suffixField">円</p>
                  </div>
              </fieldset>
          </div>
          <div class="p-formA__items">
              <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
                  <legend class="c-formLabel">
                      使用条件
                  </legend>
                  <div class="c-formSubLabel">
                      <div class="c-numberField" style="position: relative;">
                          <span style="position: absolute; left: 12px;">￥</span>
                          <input min="1" type="number" name="coupon[external_coupon_attributes][requirement_value]" v-model="requiredSubtotal" :disabled="isPersisted" style="padding-left: 30px; background-color: white;">
                          <input type="hidden" name="coupon[external_coupon_attributes][requirement_value]" v-model="requiredSubtotal" v-if="isPersisted" />
                          <p class="suffixField">以上の購入時のみ利用可能</p>
                      </div>
                  </div>
              </fieldset>
          </div>

          <coupon-expire-field :expire-day="expireDay" @update:expireDay="$emit('update:expireDay', $event)" :expire-date.sync="convertedExpireDate" :expire-type="expireType" @update:expireType="$emit('update:expireType', $event)" :disabled="isPersisted" />
      </div>
      `,
  props: {
    title: String,
    description: String,
    pictureUrl: String,
    pictureMaxSizeText: String,
    expireType: String,
    expireDay: Number,
    expireDate: String,

    storesRetailCouponAttributes: {},
  },
  data: () => ({
    convertedExpireDate: new Date(),

    storesRetailCouponId: null,
    discountType: 'percentage',
    percentage: null,
    amount: null,
    requiredSubtotal: null,
  }),
  created: function () {
    this.discountType =
      this.storesRetailCouponAttributes.discount_type || 'percentage';
    this.storesRetailCouponId = this.storesRetailCouponAttributes.id || null;
    this.percentage = this.storesRetailCouponAttributes.percentage || null;
    this.amount = this.storesRetailCouponAttributes.amount || null;
    this.requiredSubtotal =
      this.storesRetailCouponAttributes.required_subtotal || null;

    if (this.expireDate && this.expireDate !== '') {
      this.convertedExpireDate = new Date(this.expireDate);
    }
  },
  computed: {
    discountTypeOptions: () => [
      { value: 'percentage', text: '割引率（%）' },
      { value: 'amount', text: '値引き' },
      { value: 'free_shipping', text: '送料無料' },
    ],
    isPersisted: function () {
      return this.storesRetailCouponId !== null;
    },
    isDiscountTypePercentage: function () {
      return this.discountType === 'percentage';
    },
    isDiscountTypeAmount: function () {
      return this.discountType === 'amount';
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// Shopifyクーポン設定画面
// ------------------------------------------------------------------------------
const shopify_discount_service = {
  components: {
    'coupon-title-field': coupon_title_field,
    'coupon-description-field': coupon_description_field,
    'coupon-picture-field': coupon_picture_field,
    'shopify-discount-type-fields': shopify_discount_type_fields,
    'shopify-requirement-field': shopify_requirement_field,
    'coupon-expire-field': coupon_expire_field,
    'shopify-redirect-path-field': shopify_redirect_path_field,
  },
  template: `
      <div class="p-formA">
          <input type="hidden" name="coupon[external_coupon_attributes][id]" id="coupon_external_coupon_attributes_id" :value="shopifyDiscountId" />

          <coupon-title-field :title="title" @update:title="$emit('update:title', $event)" />
          <coupon-description-field :description="description" @update:description="$emit('update:description', $event)" />
          <coupon-picture-field :picture-url="pictureUrl" :max-size-text="pictureMaxSizeText" @update:pictureUrl="$emit('update:pictureUrl', $event)" />

          <shopify-discount-type-fields :discount.sync="discount" :is-persisted="isPersisted" />
          <shopify-requirement-field :requirement.sync="requirement" :is-persisted="isPersisted" />

          <coupon-expire-field :expire-day="expireDay" @update:expireDay="$emit('update:expireDay', $event)" :expire-date.sync="convertedExpireDate" :expire-type="expireType" @update:expireType="$emit('update:expireType', $event)" :disabled="isPersisted" />

          <shopify-redirect-path-field :redirect-path.sync="redirectPath" />
      </div>
      `,
  props: {
    title: String,
    description: String,
    pictureUrl: String,
    pictureMaxSizeText: String,
    expireType: String,
    expireDay: Number,
    expireDate: String,

    shopifyDiscountAttributes: {},
  },
  data: () => ({
    convertedExpireDate: new Date(),

    shopifyDiscountId: null,
    discount: { discountType: 'percentage', percentage: 0 },
    requirement: { requirementType: 'unconditional' },
    redirectPath: null,
  }),
  created: function () {
    this.shopifyDiscountId = this.shopifyDiscountAttributes.id || null;
    this.discount = buildDiscountFrom(this.shopifyDiscountAttributes);
    this.requirement = buildRequirementFrom(this.shopifyDiscountAttributes);
    this.redirectPath = this.shopifyDiscountAttributes.redirect_path || null;

    if (this.expireDate && this.expireDate !== '') {
      this.convertedExpireDate = new Date(this.expireDate);
    }
  },
  computed: {
    isPersisted: function () {
      return this.shopifyDiscountId !== null;
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// 通常クーポン設定画面
// ------------------------------------------------------------------------------
const default_coupon_service = {
  components: {
    'coupon-title-field': coupon_title_field,
    'coupon-description-field': coupon_description_field,
    'coupon-picture-field': coupon_picture_field,
    'coupon-expire-field': coupon_expire_field,
    'default-coupon-confirm-code-field': default_coupon_confirm_code_field,
    'default-coupon-restricted-once-field':
      default_coupon_restricted_once_field,
    'default-coupon-code-type-field': default_coupon_code_type_field,
  },
  template: `
      <div class="p-formA">
          <coupon-title-field :title="title" @update:title="$emit('update:title', $event)" />
          <coupon-description-field :description="description" @update:description="$emit('update:description', $event)" />
          <coupon-picture-field :picture-url="pictureUrl" :max-size-text="pictureMaxSizeText" @update:pictureUrl="$emit('update:pictureUrl', $event)" />
          <coupon-expire-field :expire-day="expireDay" @update:expireDay="$emit('update:expireDay', $event)" :expire-date.sync="convertedExpireDate" :expire-type="expireType" @update:expireType="$emit('update:expireType', $event)" />

          <div class="p-formA__items">
              <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__6 p-formA__item__l__6">
                  <a href="javascript:void(0)" @click.prevent="handleShowDetail" v-if="!isShowDetail">詳細設定を表示</a>
              </fieldset>
          </div>
          <div v-if="isShowDetail">
              <default-coupon-confirm-code-field :confirm-code="confirmCode" @update:confirmCode="$emit('update:confirmCode', $event)" />
              <default-coupon-restricted-once-field :restricted-once="restrictedOnce" @update:restrictedOnce="$emit('update:restrictedOnce', $event)" />
              <default-coupon-code-type-field :code-type="codeType" @update:codeType="$emit('update:codeType', $event)" />
          </div>
      </div>
      `,
  props: {
    title: String,
    description: String,
    pictureUrl: String,
    pictureMaxSizeText: String,
    expireType: String,
    expireDay: Number,
    expireDate: String,
    confirmCode: Boolean,
    restrictedOnce: String,
    codeType: String,
  },
  data: () => ({
    convertedExpireDate: new Date(),
    isShowDetail: false,
  }),
  created: function () {
    if (this.expireDate && this.expireDate !== '') {
      this.convertedExpireDate = new Date(this.expireDate);
    }
  },
  methods: {
    handleShowDetail: function () {
      this.isShowDetail = true;
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// オンライン・オフライン(Shopify)共通クーポン設定画面
// ------------------------------------------------------------------------------
const offline_or_shopify_coupon_service = {
  components: {
    'coupon-title-field': coupon_title_field,
    'coupon-description-field': coupon_description_field,
    'coupon-picture-field': coupon_picture_field,
    'coupon-expire-field': coupon_expire_field,
    'shopify-discount-type-fields': shopify_discount_type_fields,
    'shopify-requirement-field': shopify_requirement_field,
    'shopify-redirect-path-field': shopify_redirect_path_field,
    'default-coupon-confirm-code-field': default_coupon_confirm_code_field,
    'default-coupon-restricted-once-field':
      default_coupon_restricted_once_field,
    'default-coupon-code-type-field': default_coupon_code_type_field,
  },
  template: `
      <div class="p-formA">
        <input type="hidden" name="coupon[external_coupon_attributes][id]" id="coupon_external_coupon_attributes_id" :value="shopifyDiscountId" />

        <coupon-title-field :title="title" @update:title="$emit('update:title', $event)" />
        <coupon-description-field :description="description" @update:description="$emit('update:description', $event)" />
        <coupon-picture-field :picture-url="pictureUrl" :max-size-text="pictureMaxSizeText" @update:pictureUrl="$emit('update:pictureUrl', $event)" />
        <coupon-expire-field :expire-day="expireDay" @update:expireDay="$emit('update:expireDay', $event)" :expire-date.sync="convertedExpireDate" :expire-type="expireType" @update:expireType="$emit('update:expireType', $event)" :disabled="isShopifyAttributesPersisted" />

        <shopify-discount-type-fields :discount.sync="discount" :is-persisted="isShopifyAttributesPersisted" />
        <shopify-requirement-field :requirement.sync="requirement" :is-persisted="isShopifyAttributesPersisted" />
        <shopify-redirect-path-field :redirect-path.sync="redirectPath" />

        <div class="p-formA__items">
          <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__6 p-formA__item__l__6">
            <a href="javascript:void(0)" @click.prevent="handleShowDetail" v-if="!isShowDetail">詳細設定を表示</a>
          </fieldset>
        </div>
        <div v-if="isShowDetail">
          <default-coupon-confirm-code-field :confirm-code="confirmCode" @update:confirmCode="$emit('update:confirmCode', $event)" />
          <default-coupon-restricted-once-field :restricted-once="restrictedOnce" @update:restrictedOnce="$emit('update:restrictedOnce', $event)" />
          <default-coupon-code-type-field :code-type="codeType" @update:codeType="$emit('update:codeType', $event)" />
        </div>
      </div>
      `,
  props: {
    title: String,
    description: String,
    pictureUrl: String,
    pictureMaxSizeText: String,
    expireType: String,
    expireDay: Number,
    expireDate: String,

    confirmCode: Boolean,
    restrictedOnce: String,
    codeType: String,

    shopifyDiscountAttributes: Object,
  },
  data: () => ({
    convertedExpireDate: new Date(),
    isShowDetail: false,
    shopifyDiscountId: null,
    discount: { discountType: 'percentage', percentage: 0 },
    requirement: { requirementType: 'unconditional' },
    redirectPath: null,
  }),
  created() {
    if (this.expireDate && this.expireDate !== '') {
      this.convertedExpireDate = new Date(this.expireDate);
    }

    this.shopifyDiscountId = this.shopifyDiscountAttributes.id || null;
    this.discount = buildDiscountFrom(this.shopifyDiscountAttributes);
    this.requirement = buildRequirementFrom(this.shopifyDiscountAttributes);
    this.redirectPath = this.shopifyDiscountAttributes.redirect_path || null;
  },
  computed: {
    isShopifyAttributesPersisted() {
      return this.shopifyDiscountId !== null;
    },
  },
  methods: {
    handleShowDetail: function () {
      this.isShowDetail = true;
    },
  },
};

// ------------------------------------------------------------------------------
// クーポン設定画面
// ------------------------------------------------------------------------------
const coupon_setting_component = {
  components: {
    'default-coupon-service': default_coupon_service,
    'shopify-discount-service': shopify_discount_service,
    'stores-retail-coupon-service': stores_retail_coupon_service,
    'offline-or-shopify-coupon-service': offline_or_shopify_coupon_service,
  },
  template: `
      <div :style="{ marginTop: '20px' }">
          <div class="p-formA__items">
              <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__12 p-formA__item__l__12">
                  <legend class="c-formLabel">
                      クーポン
                  </legend>
                  <ul class="c-radioButton">
                      <li class="c-radioButton__item">
                          <input id="shop_coupon__without_coupon" type="radio" v-model="isShow" :value="false" class="c-radioButton__input">
                          <label for="shop_coupon__without_coupon" class="c-radioButton__label spec-deactivate_coupon">なし</label>
                      </li>
                      <li class="c-radioButton__item">
                          <input id="shop_coupon__with_coupon" type="radio" v-model="isShow" :value="true" class="c-radioButton__input">
                          <label for="shop_coupon__with_coupon" class="c-radioButton__label spec-activate_coupon">あり</label>
                      </li>
                  </ul>
              </fieldset>
          </div>
          <div class="p-formSections" v-if="isShow" data-testid="couponOptionalForm">
              <input type="hidden" name="coupon[shop_coupon_id]" :value="id" id="coupon_shop_coupon_id">
              <input type="hidden" name="coupon[shop_card_id]" :value="shopCardIdModel" id="coupon_shop_card_id">

              <div class="p-formSections__item">
                  <div class="p-formA__items" v-if="availableExternalCoupon">
                      <fieldset class="p-formA__item p-formA__item__s__12 p-formA__item__m__8 p-formA__item__l__8">
                          <legend class="c-formLabel">
                              クーポン種類
                          </legend>
                          <div class="c-select">
                              <select name="coupon[coupon_type]" id="coupon_type" v-model="couponType" :disabled="isPersisted">
                                  <option v-for="option in couponOptions" :value="option.value">{{ option.text }}</option>
                              </select>
                              <input type="hidden" name="coupon[coupon_type]" :value="couponType" v-if="isPersisted"/>
                              <div class="c-select__icon"></div>
                          </div>
                      </fieldset>
                  </div>
                  <default-coupon-service
                      v-if="showDefaultCouponSetting"
                      :title.sync="title"
                      :description.sync="description"
                      :picture-url.sync="pictureUrl"
                      :picture-max-size-text="pictureMaxSizeText"
                      :expire-type.sync="expireType"
                      :expire-date="expireDate"
                      :expire-day.sync="expireDay"
                      :confirm-code.sync="confirmCode"
                      :restricted-once.sync="restrictedOnce"
                      :code-type.sync="codeType"
                  />
                  <shopify-discount-service
                      v-if="showShopifyDiscountSetting"
                      :title.sync="title"
                      :description.sync="description"
                      :picture-url.sync="pictureUrl"
                      :picture-max-size-text="pictureMaxSizeText"
                      :expire-type.sync="expireType"
                      :expire-date="expireDate"
                      :expire-day.sync="expireDay"
                      :shopify-discount-attributes="shopifyDiscount"
                  />
                  <stores-retail-coupon-service
                      v-if="showStoresRetailCouponSetting"
                      :title.sync="title"
                      :description.sync="description"
                      :picture-url.sync="pictureUrl"
                      :picture-max-size-text="pictureMaxSizeText"
                      :expire-type.sync="expireType"
                      :expire-date="expireDate"
                      :expire-day.sync="expireDay"
                      :stores-retail-coupon-attributes="storesRetailCoupon"
                  />
                  <offline-or-shopify-coupon-service
                      v-if="showOfflineOrShopifyCouponSetting"
                      :title.sync="title"
                      :description.sync="description"
                      :picture-url.sync="pictureUrl"
                      :picture-max-size-text="pictureMaxSizeText"
                      :expire-type.sync="expireType"
                      :expire-date="expireDate"
                      :expire-day.sync="expireDay"
                      :confirm-code.sync="confirmCode"
                      :restricted-once.sync="restrictedOnce"
                      :code-type.sync="codeType"
                      :shopify-discount-attributes="shopifyDiscount"
                  />
              </div>
          </div>
      </div>
      `,
  props: {
    availableShopifyDiscount: {
      type: Boolean,
      default: false,
    },
    availableStoresRetailCoupon: {
      type: Boolean,
      default: false,
    },
    shopMessageId: Number,
    hasShopCoupon: Boolean,
    shopCardId: Number,
    pictureMaxSizeText: String,
  },
  data() {
    return {
      isShow: false,
      shopCardIdModel: this.shopCardId,
      id: null,
      title: '',
      description: '',
      pictureUrl: '',
      expireType: 'first',
      expireDate: '',
      expireDay: null,
      couponType: 'offline',
      confirmCode: false,
      restrictedOnce: 'restricted_once_auto',
      codeType: null,
      deletedPicture: false,

      shopifyDiscount: {},
      storesRetailCoupon: {},
    };
  },
  created: function () {
    if (this.shopMessageId && this.hasShopCoupon) {
      this.fetchCoupon((data) => {
        this.isShow = true;
        this.id = data.id;
        this.shopCardIdModel = data.shop_card_id;
        this.title = data.title;
        this.description = data.description;
        this.pictureUrl = data.picture_url;
        this.expireType = data.expire_type;
        this.expireDate = data.expire_date;
        this.expireDay = data.expire_day;
        this.couponType = data.coupon_type;
        this.confirmCode = data.confirm_code;
        this.restrictedOnce = data.restricted_once;
        this.codeType = data.code_type;
        this.shopifyDiscount = data.shopify_discount;
        this.storesRetailCoupon = data.stores_retail_coupon;
      });
    }
  },
  methods: {
    fetchCoupon: async function (callback) {
      if (!this.shopMessageId) return;

      let data;
      try {
        const response = await fetch(this.fetchCouponPath, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        data = await response.json();
      } catch (error) {
        console.error(error);
        alertify.error('エラー');
        return;
      }

      callback(data.data.attributes);
    },
    availableExternalCoupon: function () {
      return this.availableShopifyDiscount || this.availableStoresRetailCoupon;
    },
  },
  computed: {
    fetchCouponPath: function () {
      return `/api/shop/v1/shop_messages/${this.shopMessageId}/shop_coupons`;
    },
    showDefaultCouponSetting: function () {
      return this.couponType === 'offline';
    },
    showShopifyDiscountSetting: function () {
      return this.couponType === 'shopify';
    },
    showStoresRetailCouponSetting: function () {
      return this.couponType === 'stores_ec';
    },
    showOfflineOrShopifyCouponSetting() {
      return this.couponType === 'offline_or_shopify';
    },
    isPersisted: function () {
      return this.id !== null;
    },
    couponOptions: function () {
      const options = [];
      options.push({ value: 'offline', text: '通常クーポン' });
      if (this.availableShopifyDiscount) {
        options.push({ value: 'shopify', text: 'Shopify' });
        options.push({
          value: 'offline_or_shopify',
          text: 'オンライン・オフライン(Shopify)共通クーポン',
        });
      }
      if (this.availableStoresRetailCoupon) {
        options.push({ value: 'stores_ec', text: 'ネットショップ' });
      }
      return options;
    },
  },
};

export default class extends Controller {
  static targets = ['template'];
  static values = {
    shopMessageId: Number,
    hasShopCoupon: Boolean,
    shopCardId: Number,
    availableShopifyDiscount: Boolean,
    availableStoresRetailCoupon: Boolean,
    pictureMaxSizeText: String,
  };

  connect() {
    // el: での直接マウントは target 要素自体を置き換えてしまい、親の legacy Vue (#bulkShopMessage) が
    // outerHTML からこのサブツリーを再生成した際に data-*-target 属性が失われて再接続に失敗するため、
    // target 要素は残したまま子要素としてマウントする。再接続時は前回マウント分が outerHTML 経由で
    // 静的に焼き込まれて残っているため、置き換える前に一度クリアする
    this.vueApp = mountVueAppInto(this.templateTarget, {
      ...coupon_setting_component,
      propsData: {
        shopMessageId: this.shopMessageIdValue,
        hasShopCoupon: this.hasShopCouponValue,
        shopCardId: this.shopCardIdValue,
        availableShopifyDiscount: this.availableShopifyDiscountValue,
        availableStoresRetailCoupon: this.availableStoresRetailCouponValue,
        pictureMaxSizeText: this.pictureMaxSizeTextValue,
      },
    });
  }

  disconnect() {
    this.vueApp?.unmount();
  }
}
